import { faDownload, faEye, faTrash, faUserCog, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { SearchParamsPagination } from 'src/components/Pagination';
import { Scroller } from 'src/components/Scroller';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { StickyCard } from 'src/components/StickyCard';
import { DataTable } from 'src/components/Table/DataTable';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { externalLinks } from 'src/externalLinks';
import { UserAssessment } from 'src/models/assessment/_types';
import {
  deleteUserAssessment,
  exportUserAssessment,
  fetchUserAssessments,
} from 'src/models/assessment/assessment';
import { getUserDisplayName } from 'src/models/user/utils';
import { AssessmentFilter } from 'src/shared/Filters/AssessmentFilter';
import { ClientFilter } from 'src/shared/Filters/ClientFilter';
import { Filters } from 'src/shared/Filters/Filters';
import { LocationFilter } from 'src/shared/Filters/LocationFilter';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

export const CompletedUserAssessmentsPage = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessments',
  });
  const { t: tAssessment } = useTranslation('translation', {
    keyPrefix: 'assessment',
  });
  const { t: tTrainer } = useTranslation('translation', {
    keyPrefix: 'trainer',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const {
    params,
    filterParams,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({
    defaultPerPage: 20,
  });

  const [confirmDeleteProps, confirm] = useConfirmDialogComponent();

  // Parse search term for first name and last name
  const searchTerm = params.search?.trim() || '';
  const searchParts = searchTerm.split(' ');

  // Fetch completed user assessments
  const { data, isLoading } = useQuery({
    queryKey: [
      'completedUserAssessments',
      params.page,
      params.perPage,
      params.search,
      filterParams.assessment,
      filterParams.location,
      filterParams.client,
    ],
    queryFn: () =>
      fetchUserAssessments({
        userIdEq: filterParams.client
          ? Number(filterParams.client)
          : undefined,
        page: params?.page || 1,
        perPage: params?.perPage,
        forTrainer: true,
        userIdNotNull: true,
        withUsers: true,
        withAssessments: true,
        assessmentId: filterParams.assessment
          ? Number(filterParams.assessment)
          : undefined,
        locationId: filterParams.location
          ? Number(filterParams.location)
          : undefined,
        userFirstName:
          searchParts.length >= 1 && searchTerm ? searchParts[0] : undefined,
        userLastName:
          searchParts.length > 1 ? searchParts.slice(1).join(' ') : undefined,
      }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteUserAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['completedUserAssessments'],
      });
      toast.success(tAssessment('deleteSuccessMessage'));
    },
    onError: (error) => {
      toast.error(api.formatError(error));
    },
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: exportUserAssessment,
    onSuccess: (resourceId) => {
      const downloadUrl = `/resources/${resourceId}/download/`;
      window.open(externalLinks.v2Url(downloadUrl), '_blank');
    },
    onError: () => {
      toast.error(tAssessment('exportFailedMessage'));
    },
  });

  // Handle delete
  const handleDelete = async (assessment: UserAssessment) => {
    if (
      await confirm({
        title: tAssessment('deleteAssessment'),
        message: t('deleteUserAssessmentConfirm'),
      })
    ) {
      deleteMutation.mutate(assessment.id);
    }
  };

  // Handle export/download
  const handleDownload = (assessmentId: Identifier) => {
    toast.success(tCommon('exportingInProgress'));
    exportMutation.mutate(assessmentId);
  };

  // Handle invite as client (single)
  const handleInviteAsClient = (assessment: UserAssessment) => {
    const name = assessment.leadName?.split(' ') || [];
    const firstName = name[0] || '';
    const lastName = name[1] || '';
    const email = assessment.user?.email || assessment.leadEmail || '';

    const params = new URLSearchParams({
      email,
      first_name: assessment.user?.firstName || firstName,
      last_name: assessment.user?.lastName || lastName,
    });

    navigate(`/clients/new?${params.toString()}`);
  };

  // Filter configurations
  const filterConfigs = [
    {
      field: 'assessment',
      label: tAssessment('one'),
      Component: AssessmentFilter,
    },
    {
      field: 'location',
      label: tCommon('labels.location'),
      Component: LocationFilter,
    },
    {
      field: 'client',
      label: tTrainer('client.one'),
      Component: ClientFilter,
      props: {
        useUserId: true,
      },
    },
  ];

  // Table columns
  const columns = [
    {
      id: 'name',
      label: tCommon('labels.name'),
      format: (_, row: UserAssessment) => {
        return row.assessmentName || row.assessment?.name || '';
      },
    },
    {
      id: 'user',
      label: tCommon('labels.user'),
      format: (_, row: UserAssessment) => {
        if (!row.user) {
          return null;
        }
        return getUserDisplayName(row.user);
      },
    },
    {
      id: 'completedOn',
      label: tAssessment('completedOn'),
      format: (_: number, row: UserAssessment) => {
        if (row.incomplete) {
          return tAssessment('incomplete');
        }
        return formatFullDateAndTime(row.createdAt, {
          weekday: undefined,
        });
      },
      show: !smDown,
    },
    {
      id: 'clientStatus',
      label: `${tTrainer('client.one')} ${tCommon('labels.status')}`,
      format: (_, row: UserAssessment) => {
        if (!row.user) {
          return null;
        }
        const isClient = !!row.clientId;
        return isClient ? tTrainer('client.one') : tTrainer('client.non');
      },
    },
  ];

  // Table actions
  const actions = [
    {
      icon: faEye,
      label: t('results'),
      onClick: (assessment: UserAssessment) =>
        navigate(`/me/assessment/completed/${assessment.id}`),
    },
    {
      icon: faUserCog,
      label: t('manageClient'),
      onClick: (assessment: UserAssessment) =>
        navigate(`/clients/${assessment.clientId}`),
      showAction: (row: UserAssessment) => !!row.clientId,
    },
    {
      icon: faUserPlus,
      label: t('addAsClient'),
      onClick: handleInviteAsClient,
      showAction: (row: UserAssessment) => !row.clientId,
    },
    {
      icon: faDownload,
      label: tAssessment('downloadAsCsv'),
      onClick: (assessment: UserAssessment) => handleDownload(assessment.id),
    },
    {
      icon: faTrash,
      label: tAssessment('deleteAssessment'),
      onClick: handleDelete,
    },
  ];

  return (
    <>
      <StickyCard
        title={t('completedUsers')}
        contentSx={{ p: 0 }}
      >
        <SectionToolbar>
          <SearchParamSearchTextField
            params={params}
            updateParam={updateParam}
            sx={{ width: smDown ? '100%' : '280px' }}
            placeholder={t('searchUserPlaceholder')}
          />
        </SectionToolbar>
        <SectionToolbar>
          <Scroller
            direction="row"
            spacing={2}
            hideScrollbar
            sx={{ boxSizing: 'content-box' }}
          >
            <Filters
              filters={filterConfigs}
              params={params}
              updateParam={updateParam}
              updateFilterParam={updateFilterParam}
              getFilterParamValue={getFilterParamValue}
              resetFilterParams={resetFilterParams}
            />
          </Scroller>
        </SectionToolbar>
        <DataTable
          columns={columns}
          rows={data?.userAssessments || []}
          selectableRows={false}
          noResultsText={t('noCompletedAssessments')}
          onClickRow={(row) => navigate(`/me/assessment/completed/${row.id}`)}
          loading={isLoading}
          rowActions={actions}
          fixedActions
        />

        {/* Pagination */}
        <SearchParamsPagination
          params={params}
          updateParam={updateParam}
          total={data?.total || 0}
          itemsPerPage={params.perPage}
        />
      </StickyCard>

      {/* Confirm dialog */}
      <ConfirmDialog {...confirmDeleteProps} />
    </>
  );
};
